import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignWhatsAppConversationInput,
  ListWhatsAppConversationsQuery,
  SendWhatsAppMessageInput,
  WhatsAppConversationResponse,
  WhatsAppMessageResponse,
} from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListWhatsAppConversationsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useWhatsAppConversationsQuery(query: Partial<ListWhatsAppConversationsQuery>) {
  return useQuery({
    queryKey: ['whatsapp-conversations', query],
    queryFn: () => getPage<WhatsAppConversationResponse[]>(`/whatsapp/conversations${toQueryString(query)}`),
    placeholderData: (prev) => prev,
    refetchInterval: 15_000,
  });
}

export function useWhatsAppConversationQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['whatsapp-conversations', id],
    queryFn: () => api.get<{ conversation: WhatsAppConversationResponse; messages: WhatsAppMessageResponse[] }>(`/whatsapp/conversations/${id}`),
    enabled: Boolean(id),
    refetchInterval: 8_000,
  });
}

export function useSendWhatsAppMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SendWhatsAppMessageInput }) => api.post<WhatsAppMessageResponse>(`/whatsapp/conversations/${id}/messages`, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations', id] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });
}

export function useAssignWhatsAppConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AssignWhatsAppConversationInput }) => api.patch<WhatsAppConversationResponse>(`/whatsapp/conversations/${id}/assign`, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations', id] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });
}
